import React, { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RecordAcquisitionAttributionDto } from './api';
import { sendFirstTouch } from './platform/attribution';
import {
  AUTH_PROVIDER_CLAIM,
  resolveSignupProvider,
  trackAuthenticatedFunnelState,
} from './platform/funnel';
import { appStorage } from './platform/runtime';
import useAstroApiClient from './hooks/useAstroApiClient';

const ProfileGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth();
  const history = useHistory();
  const { i18n } = useTranslation();
  const api = useAstroApiClient();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const check = async () => {
      if (!auth.user) return;
      try {
        const user = await api.apiUsersAuth();

        // GR-002: the single authenticated point every signed-in route passes
        // through. Only a non-personal projection of the account is handed over.
        trackAuthenticatedFunnelState({
          createdAtUtc: user?.createdAtUtc,
          provider: resolveSignupProvider(auth.user?.profile?.[AUTH_PROVIDER_CLAIM]),
          hasBirthRecord: Boolean(
            user?.birthDateTime &&
              user?.birthPlace &&
              typeof user?.latitude === 'number' &&
              typeof user?.longitude === 'number'
          ),
        });

        // GR-003: the origin captured before the OIDC redirect, sent once the
        // account exists. Delivery is tracked per subject, so a second account on
        // the same browser is attributed too. Idempotent on the server, so the
        // first origin wins even if this runs twice; a failure stays pending.
        void sendFirstTouch(user?.authId, origin =>
          api.apiAcquisitionAttributionPost(
            new RecordAcquisitionAttributionDto({
              source: origin.source,
              medium: origin.medium,
              campaign: origin.campaign,
              content: origin.content,
            })
          )
        );

        const supportedLngs = i18n.options.supportedLngs;
        const supportedLanguages = Array.isArray(supportedLngs)
          ? supportedLngs.filter(lang => lang && lang !== 'cimode')
          : [];

        if (user?.language && supportedLanguages.includes(user.language)) {
          i18n.changeLanguage(user.language);
          appStorage.local.setItem('languageChosen', 'true');
        }
        setChecking(false);
      } catch {
        history.replace('/error');
      }
    };
    check();
  }, [auth.user, history, api, i18n]);

  if (checking) return null;
  return <>{children}</>;
};

export default ProfileGuard;
